import {type MutableRefObject, useCallback, useState} from 'react';

import {cellAtPoint} from '../lib/cell-at-point';
import {beginDrag} from '../lib/pointer-drag';
import {canFillFrom, fillTarget, tableFillRange} from '../lib/table-fill';
import {normalizeRange} from '../lib/table-ops';
import type {CellRange, TableConfig, TableConfigSnapshot, TableRowData} from '../lib/table-types';

interface UseTableFillDragParams {
  configRef: MutableRefObject<TableConfigSnapshot>;
  toCfg: (overrides: Partial<TableConfig>) => TableConfig;
  update: (cfg: TableConfig) => void;
  rows: readonly TableRowData[];
  /** The selection the handle may fill from: `null` while one is being dragged
   *  out or a cell is being typed into, so the handle is not drawn then. */
  selection: CellRange | null;
}

/** What the table draws for the fill handle — see `CellFillOverlay`. */
export interface TableFillDrag {
  /** The range a release would fill, while the pointer is past the selection. */
  preview: CellRange | null;
  /** The selection the handle sits on the corner of, normalised; `null` when
   *  no handle is offered. */
  corner: CellRange | null;
  start: (e: React.MouseEvent) => void;
}

/**
 * The fill handle's drag: press the selection's corner, drag down or right, and
 * release to copy the selection over the cells passed — see `tableFillRange`.
 * The release selects the filled range, so the author sees what changed and can
 * undo it in one step.
 */
export function useTableFillDrag({
  configRef,
  toCfg,
  update,
  rows,
  selection,
}: UseTableFillDragParams): TableFillDrag {
  const [preview, setPreview] = useState<CellRange | null>(null);

  const start = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const range = configRef.current.cellRange;
      if (!range) return;
      const source = normalizeRange(range);
      let target: CellRange | null = null;
      const startX = e.clientX;
      const startY = e.clientY;
      beginDrag(e, {
        cursor: 'crosshair',
        onMove: (dx, dy) => {
          const hit = cellAtPoint(startX + dx, startY + dy);
          if (!hit) return;
          target = fillTarget(source, hit.row, hit.col);
          setPreview(target);
        },
        onSettle: () => setPreview(null),
        onCommit: () => {
          if (!target) return;
          update({...tableFillRange(toCfg({}), source, target), cellRange: target});
        },
      });
    },
    [configRef, toCfg, update],
  );

  const corner =
    selection && !preview && canFillFrom(rows, selection) ? normalizeRange(selection) : null;
  return {preview, corner, start};
}
